/* eslint-disable class-methods-use-this, no-restricted-globals */
import React, {
    Component,
} from 'react';

import queryString from 'query-string';
import { connect } from 'react-redux';

import Footer from 'wanderu-component-footer';
import Intercom from 'wanderu-component-intercom';
import NotFound from 'wanderu-component-not-found';
import { BusLoader } from 'wanderu-components';
import appSettings from 'wanderu-config';
import {
    fetchCityToCityInterlinksAction,
    interlinkDataSelectors,
} from 'wanderu-ducks-interlink';
import {
    fetchAllTripsAction,
    fetchCachedTripsAction,
} from 'wanderu-ducks-psearch';
import {
    cityToCitySelectors as c2cSelectors,
    fetchCityToCityAction,
    scheduleSelectors,
} from 'wanderu-ducks-seo';
import {
    splitTestSelectors,
    splitTestActions,
} from 'wanderu-ducks-split-test';
import {
    localize,
    i18nCoordinator,
    i18nMoment,
    selectLocale,
    languageSelector,
} from 'wanderu-i18n';
import Widget from 'wanderu-widget';

import cityToCityConfig from '../../../config/c2c.locale';
import CityToCityComponent from '../../components/CityToCity/CityToCity';
import CityToCityMeta from '../../components/CityToCity/CityToCityMeta/CityToCityMeta';
import routeMapAbTestData from '../../data/routeMapAbTestData';
import {
    fetchCityToCityData,
    fetchTimeTableData as fetchTimeTableDataFn,
} from '../../ducks/actions/fetchCityToCityData';
import {
    generateLocaleAwareGuideId,
    getPSearchParams,
} from '../../utils';
import { localizeStructuredData } from '../utils';

function stateFromRegion(regionString) {
    const [
        country,
        state,
    ] = regionString.split('-');

    return state
        ? state.toUpperCase()
        : country.toUpperCase();
}

function calculateExpiresTime(tz) {
    if (!tz) {
        return 60 * 60 * 24;
    }

    const now = i18nMoment();
    const expireTime = i18nMoment().tz(tz).endOf('day').add('day', 1);
    return expireTime.diff(now, 'seconds');
}

class CityToCity extends Component {
    static defaultProps = {
        interlinkData: {},
    };

    static async serverFetchData(opts) {
        const {
            dispatch,
            getState,
            i18nCoordinator: serverI18nCoordinator,
            params,
            query,
        } = opts;

        const showTranslationKeys = appSettings.env.wanderuEnv !== 'prod' && query?.showTranslationKeys;

        const cityToCityId = generateLocaleAwareGuideId(params);

        let c2cError;
        try {
            await dispatch(fetchCityToCityData(cityToCityId, params, { isClient: false, path: opts?.ctx?.path }));
            const cachedConfigResponse = splitTestSelectors.selectTestData(getState());
            dispatch(splitTestActions.setCachedTestParams(cachedConfigResponse));
        } catch (ex) {
            c2cError = fetchCityToCityAction.selectError(getState(), cityToCityId);
        }

        if (c2cError) {
            return;
        }

        /* if alternate transportation modes are detected, it means there's no
         * valid c2c page for this combination of (from, to, mode) tuple.  don't
         * bother fetching interlinks for pages we're just going to 301 */
        if (c2cSelectors.selectAlternateTransModes(getState(), cityToCityId)) {
            return;
        }

        await Promise.all([
            serverI18nCoordinator.registerTranslatableApp({
                ...cityToCityConfig,
                namespace: showTranslationKeys
                    ? 'showTranslationKeys'
                    : cityToCityConfig.namespace,
                translationFetchFn: () => {
                    const apiTranslations = c2cSelectors.selectContent(getState(), cityToCityId);

                    return {
                        ...apiTranslations,
                    };
                },
            }),
            dispatch(fetchCityToCityInterlinksAction
                .fetchCityToCityInterlinks(cityToCityId, params)),
            Widget.serverFetchData(opts),
        ]);
    }

    async componentDidMount() {
        const {
            match,
            notFound,
            isLoaded,
            location,
            schedulesVariant,
        } = this.props;

        if (notFound) {
            return;
        }

        const cityToCityId = generateLocaleAwareGuideId(match.params);
        if (!isLoaded) {
            this.props.fetchCityToCityData(cityToCityId, match.params, { path: location.pathname });
            this.props.fetchCityToCityInterlinks(cityToCityId, match.params);
        } else if (schedulesVariant !== 'schedules') {
            this.props.fetchTimeTableData(cityToCityId, { path: location.pathname });
        }
        if (appSettings.env.wanderuEnv !== 'prod' && !!queryString.parse(location.search)?.showTranslationKeys) {
            return;
        }

        await i18nCoordinator.registerTranslatableApp({
            ...cityToCityConfig,
            translationFetchFn: () => {
                // translations are done in componentDidUpdate right now :-|
            },
        });
    }

    async componentDidUpdate(prevProps) {
        const {
            match,
            content,
            location,
        } = this.props;
        const {
            locale,
        } = match.params;
        const newCityToCityId = generateLocaleAwareGuideId(match.params);
        const previousCityToCityId = generateLocaleAwareGuideId(prevProps.match.params);

        if (newCityToCityId !== previousCityToCityId) {
            this.props.fetchCityToCityData(newCityToCityId, match.params, { path: location.pathname });
            this.props.fetchCityToCityInterlinks(newCityToCityId, match.params);
        }

        const showTranslationKeys = appSettings.env.wanderuEnv !== 'prod' && !!queryString.parse(location.search)?.showTranslationKeys;

        const namespace = showTranslationKeys
            ? 'showTranslationKeys'
            : cityToCityConfig.namespace;

        if (prevProps.isFetching && !this.props.isFetching) {
            i18nCoordinator.updateTranslations(locale, content, namespace);
            this.forceUpdate(); // the translations changed, but there's nothing that will cause a re-render, so we force it.
        }
    }

    componentWillUnmount() {
        const showTranslationKeys = appSettings.env.wanderuEnv !== 'prod' && !!queryString.parse(location.search)?.showTranslationKeys;
        const namespace = showTranslationKeys
            ? 'showTranslationKeys'
            : cityToCityConfig.namespace;
        i18nCoordinator.unregisterTranslatableApp(namespace);
    }

    _onTimeTableDateSelect = (date) => {
        const {
            cityToCityId,
            fetchTimeTableData,
        } = this.props;

        fetchTimeTableData(cityToCityId, { date });
    };

    render() {
        const {
            alternateTransModes,
            cheapestTicketData,
            content,
            error,
            isFetching,
            locale,
            locationData,
            location,
            noIndex,
            noIndexLocale,
            notFound,
            staticContext,
            structuredData,
            t,
            i18nMoney,
        } = this.props;

        if (staticContext) {
            const departTZ = locationData?.departure?.timezone;
            staticContext.cache = {
                secondsFromNow: calculateExpiresTime(departTZ),
            };

            if (staticContext.url) {
                return null;
            }

            if (notFound) {
                staticContext.status = 404;
            } else if (error) {
                staticContext.status = error.status;
                staticContext.cache = undefined;
            } else if (alternateTransModes) {
                /* igor-priority order */
                const urlSlug = alternateTransModes.bus
                    || alternateTransModes.train
                    || alternateTransModes.ferry;

                staticContext.status = 301;
                staticContext.url = `/${locale}${urlSlug}/${location.search}`;

                return null;
            }
        }

        if (notFound || error) {
            return <NotFound status={staticContext?.status || error?.status} />;
        }

        if (isFetching || !content) {
            return (
                <BusLoader large />
            );
        }

        const locationDataWithStates = {
            arrival: {
                ...locationData.arrival || {},
                state: stateFromRegion(this.props.match.params.toRegion),
            },
            departure: {
                ...locationData.departure || {},
                state: stateFromRegion(this.props.match.params.fromRegion),
            },
        };

        const canonicalSlug = content.canonicalSlug;
        const localizedStructuredData = localizeStructuredData({
            structuredData,
            t,
            i18nMoney,
            locale,
        });

        return (
            <>
                <CityToCityMeta
                    canonicalPath={canonicalSlug}
                    data={cheapestTicketData}
                    noIndex={noIndex}
                    noIndexLocale={noIndexLocale}
                    structuredData={localizedStructuredData}
                />
                <CityToCityComponent
                    {...this.props}
                    locationData={locationDataWithStates}
                    onTimeTableDateSelect={this._onTimeTableDateSelect}
                />
                <Footer
                    limitLinkFlow
                />
                <Intercom/>
            </>
        );
    }
}

function getRouteMapSlug(pathname) {
    return routeMapAbTestData.find(
        ({ urlSlug }) => pathname.toLowerCase().includes(urlSlug),
    )?.c2cId;
}

function mapStateToProps(state, ownProps) {
    const {
        location,
        match,
    } = ownProps;
    const {
        params,
    } = match;
    const {
        mode: transMode,
    } = params;

    const { pathname } = ownProps.location;
    const routeMapImageSlug = getRouteMapSlug(pathname);

    const cityToCityId = generateLocaleAwareGuideId(ownProps.match.params);
    const error = fetchCityToCityAction.selectError(state, cityToCityId);
    const notFound = fetchCityToCityAction.selectNotFound(state, cityToCityId);

    if (notFound || error) {
        return {
            error,
            notFound,
        };
    }

    const isFetching = fetchCityToCityAction.selectWorking(state, cityToCityId)
        || fetchCityToCityInterlinksAction.selectWorking(state, cityToCityId);
    const content = c2cSelectors.selectContent(state, cityToCityId);

    const altTransModeStats = c2cSelectors.selectAltTransModeStats(
        state,
        cityToCityId,
    );

    const alternateTransModes = c2cSelectors.selectAlternateTransModes(
        state,
        cityToCityId,
    );
    const locale = selectLocale(state);

    if (alternateTransModes) {
        return {
            alternateTransModes,
            locale,
        };
    }

    const loadedC2C = fetchCityToCityAction.selectSuccess(state, cityToCityId)
        || !!fetchCityToCityAction.selectError(state, cityToCityId);
    const isLoaded = loadedC2C;

    const locationData = c2cSelectors.selectLocationData(state, cityToCityId);
    const timeTableDate = c2cSelectors.selectTimeTableDate(state, cityToCityId);
    const psearchParams = getPSearchParams(locationData, timeTableDate);
    const isFetchingCachedTrips = fetchCachedTripsAction.selectWorking(state, psearchParams);
    let isFetchingAllTrips = fetchAllTripsAction.selectWorking(state, psearchParams);
    // temporarily usint this while jit in seo pages is disabled SUP-751 - nw 11/7/2023
    const hasFetchedCachedTrips = fetchCachedTripsAction.selectSuccess(state, psearchParams)
        || fetchCachedTripsAction.selectError(state, psearchParams);
    // temporarily replaced while jit in seo pages is disabled SUP-751 - nw 11/7/2023
    // const hasFetchedAllTrips = fetchAllTripsAction.selectSuccess(state, psearchParams)
    //     || fetchAllTripsAction.selectError(state, psearchParams);
    // isFetchingAllTrips = isFetchingAllTrips || !hasFetchedAllTrips;

    isFetchingAllTrips = isFetchingCachedTrips || !hasFetchedCachedTrips;
    /// end weird hack SUP-751 - nw 11/7/2023

    let infoSectionContent = c2cSelectors.selectInfoSectionContent(state, cityToCityId);

    if (appSettings.env.wanderuEnv !== 'prod' && !!queryString.parse(location.search)?.showTranslationKeys && infoSectionContent) {
        infoSectionContent = infoSectionContent.map((info) => ({
            ...info,
            text: `${info.prefix}Text`,
            title: `${info.prefix}Title`,
        }));
    }

    return {
        c2cRevampVariant: splitTestSelectors.selectTestVariant(state, 'c2cRevamp'),
        altTransModeStats,
        routeMapImageSlug,
        breadcrumbData: c2cSelectors.selectBreadcrumbData(state, cityToCityId, params),
        carrierLinks: c2cSelectors.selectCarrierLinks(state, cityToCityId),
        cheapestTicketData: c2cSelectors.selectCheapestTicketData(state, cityToCityId),
        cityToCityCarrierData: c2cSelectors.selectCityToCityCarrierData(state, cityToCityId),
        cityToCityId,
        content,
        dailyTripsData: c2cSelectors.selectDailyTripsData(state, cityToCityId),
        distanceData: c2cSelectors.selectDistanceData(state, cityToCityId),
        infoSectionContent,
        interlinkData: interlinkDataSelectors.selectC2CData(state, cityToCityId),
        isFetching,
        isFetchingAllTrips,
        isFetchingCachedTrips,
        isLoaded,
        locale,
        locationData,
        longFormContent: c2cSelectors.selectLongFormContent(state, cityToCityId),
        noIndex: c2cSelectors.selectNoIndex(state, cityToCityId),
        noIndexLocale: c2cSelectors.selectNoIndexLocale(state, cityToCityId),
        priceHistory: c2cSelectors.selectPriceHistory(state, cityToCityId),
        quickviewData: c2cSelectors.selectQuickviewData(state, cityToCityId),
        reviewSummary: c2cSelectors.selectReviewSummary(state, cityToCityId),
        simplifiedCarrierReviews: c2cSelectors.selectSimplifiedCarrierReviews(
            state,
            cityToCityId,
        ),
        structuredData: c2cSelectors.selectStructuredData(
            state,
            cityToCityId,
            params,
            transMode,
        ),
        scheduleData: scheduleSelectors.selectSchedule(state, cityToCityId),
        schedulesVariant: splitTestSelectors.selectTestVariant(state, 'UpcomingSchedulesV3'),
        transMode,
        travelFrequencyData: c2cSelectors.selectTravelFrequencyData(state, cityToCityId),
        timeTableDate,
        upcomingTrips: c2cSelectors.selectUpcomingTrips(state, psearchParams, transMode),
        language: languageSelector(state),
        blurbSectionContent:
            c2cSelectors.selectBlurbSectionContent(state, cityToCityId, transMode),
        renderFlags: c2cSelectors.selectRenderFlags(state, cityToCityId),
        journeyInfo: c2cSelectors.selectJourneyStats(state, cityToCityId),
        cityContent: c2cSelectors.selectCityContent(state, cityToCityId),
    };
}

const mapDispatchToProps = {
    fetchCityToCityData,
    fetchCityToCityInterlinks: fetchCityToCityInterlinksAction.fetchCityToCityInterlinks,
    fetchTimeTableData: fetchTimeTableDataFn,
};

export {
    CityToCity,
    mapStateToProps,
};

const localizedCityToCity = localize(CityToCity, cityToCityConfig.namespace);
export default connect(mapStateToProps, mapDispatchToProps)(localizedCityToCity);
